import React, {useContext, useEffect, useRef, useState} from 'react';
import LocationCard from "../../../../../components/app-specific/location-card";
import {CompanyContext} from "../../../../../contexts/company";
import Api from "../../../../../../core/services/api_service";
import useRouter from "../../../../../hooks/use-router";
import useIsMounted from "../../../../../hooks/use-is-mounted";
import {LocationTypes} from "../../../../../../core/constants/enums";
import {ReactComponent as BackIcon} from "../../../../../../assets/images/views/location/back.svg";
import {routeFunctions} from "../../../../../routes";
import {useTranslation} from "react-i18next";
import {ReactComponent as GenerateReportIcon} from "../../../../../../assets/images/report.svg";
import BreadCrumbs from "../../../../../components/app-specific/bread-crumbs";
import NoEntries from "../../../../../components/app-specific/no-entries";
import {CSSTransition, TransitionGroup} from "react-transition-group";
import {ReactComponent as PlusIcon} from "../../../../../../assets/images/views/location/plus.svg";


const AislesViews = () => {
    const {history, params: {warehouse, company: companyParam}} = useRouter();
    const [company] = useContext(CompanyContext);
    const [aisles, setAisles] = useState([]);
    const [locationInfo, setLocationInfo] = useState({});
    const [loading, setLoading] = useState(true);
    const [generatingReport, setGeneratingReport] = useState(false);
    const [breadcrumbs, setBreadcrumbs] = useState([]);
    const isMounted = useIsMounted();
    const {t} = useTranslation();
    const selectedCompany = useRef(company);
    const translations = t('views.panel.company.location', {
        returnObjects: true,
        code: locationInfo?.warehouse?.code ?? ''
    });

    /**
     * With each change in the company id:
     * - navigates user to the list of warehouses related to the selected company
     */
    useEffect(() => {
        if (selectedCompany.current?.id === company?.id)
            return;
        selectedCompany.current = company;
        history.replace(routeFunctions.panel.company.location.warehouses(company.name))
    }, [company?.id])

    /**
     * Listens to the changes of the any of the previously selected location hierarchies, and if any of them changed:
     * Fetches the list of the aisles for the provided warehouse
     */
    useEffect(() => {
        getLocationInfo();
    }, [warehouse]);

    /**
     * Gets the location address information
     */
    const getLocationInfo = () => {
        setLoading(true);
        //TODO: call the Api to load the location address information
        const response = {
            resultFlag: true,
            data: {warehouse: {id: warehouse, code: '00'}}
        };
        if (response?.resultFlag) {
            setLocationInfo(response?.data);
            if (window.bizpire?.makeLocationBreadcrumbs) {
                setBreadcrumbs(window.bizpire?.makeLocationBreadcrumbs(response?.data))
            }
            getAisles()
        } else {
            setLoading(false)
        }
    }

    /**
     * Fetches the list of aisles for the provided warehouse from the server and populates the inner state.
     */
    const getAisles = () => {
        Api.getAisles(warehouse).then((response) => {
            if (!isMounted()) return;
            if (response?.resultFlag) {
                setAisles(response?.data ?? []);
            }
            setLoading(false);
        })
    }

    /**
     * Generates a csv report for all the parts in the selected company and the provided values as filters.
     * @param {any} values the selected filters accumulated from the dialog.
     */
    const generateReport = (values) => {
        setGeneratingReport(true);
        const forApi = {...values}
        //TODO: call api for generating report for all the parts in a warehouse
        setGeneratingReport(false);
    }

    /**
     * Submits the new aisle to the server and appends the newly created aisle to the list of aisles
     * @param {string} aisleCode
     */
    const createAisle = async (aisleCode) => {
        const forApi = {
            code: aisleCode,
            warehouseId: parseInt(warehouse)
        }
        const response = await Api.createAisle(forApi, forApi.warehouseId);
        if (!isMounted()) return;
        if (response?.resultFlag) {
            setAisles(prevState => [...prevState, response.data]);
        } else {
            // TODO: return errors based on the errors given in the api.
        }
    }

    /**
     * Updates the aisle information using the provided aisle id and aisle code
     * If the result was successful, updates the aisle in the list of aisles
     * @param {number} aisleId
     * @param {string} aisleCode
     */
    const updateAisle = async (aisleId, aisleCode) => {
        const forApi = {
            id: aisleId,
            code: aisleCode,
            warehouseId: parseInt(warehouse)
        }
        const response = await Api.updateAisle(forApi, forApi.warehouseId);
        if (!isMounted()) return;
        if (response?.resultFlag) {
            setAisles(prevState => prevState?.map(e => e.aisle.id === aisleId
                ? {...e, aisle: {...e.aisle, code: aisleCode}}
                : e
            ) ?? []);
        } else {
            // TODO: return errors based on the errors given in the api.
        }
    }

    /**
     * Removes the given aisle from the list of aisles after removing it from the server.
     * @param {any} aisle the id associated with the aisle to be removed
     */
    const removeAisle = (aisle) => {
        setAisles(prevState => prevState?.map(e => e.aisle.id === aisle?.id
            ? {...e, loading: true}
            : e
        ) ?? []);
        Api.removeAisle(aisle?.id, parseInt(warehouse)).then((response) => {
            if (!isMounted()) return;
            if (response?.resultFlag) {
                setAisles(prevState => prevState?.filter((e) => e.aisle.id !== aisle?.id) ?? []);
            }
            setAisles(prevState => prevState?.map(e => e.aisle.id === aisle?.id
                ? {...e, loading: false}
                : e
            ) ?? []);
        })
    }


    /**
     * Navigates the user to the previous location page
     */
    const navigateBack = () => {
        history.push(routeFunctions.panel.company.location.warehouses(companyParam));
    }

    /**
     * Opens the general report's dialog and binds the generation report callback for successful filtering.
     */
    const showGenerateReportDialog = () => {
        const generateLocationReportDialog = window.bizpire?.generateLocationReportDialog;
        if (!generateLocationReportDialog) return;
        generateLocationReportDialog(true, locationInfo, generateReport)
    }

    /**
     * Opens the Add location dialog and binds the aisle creation logic to the callback of this dialog.
     */
    const showAddAisleDialog = () => {
        const upsertLocationDialog = window.bizpire?.upsertLocationDialog;
        if (!upsertLocationDialog) return
        upsertLocationDialog(true, LocationTypes.aisle, undefined, ({code}) => createAisle(code))
    }

    /**
     * Opens the Add location dialog and binds the aisle creation logic to the callback of this dialog.
     * @param {any} aisle the aisle to be updated.
     */
    const showEditAisleDialog = (aisle) => {
        if (!aisle) return;
        const upsertLocationDialog = window.bizpire?.upsertLocationDialog;
        if (!upsertLocationDialog) return
        upsertLocationDialog(true, LocationTypes.aisle, {code: aisle.code}, ({code}) => updateAisle(aisle.id, code))
    }


    return (
        <div className={'panel-card location-view'}>
            <div className='d-flex justify-content-between align-items-center'>
                <div className={'d-flex align-items-center'}>
                    <button className='button primary icon-button back'
                            onClick={navigateBack}>
                        <BackIcon/>
                    </button>
                    <p className='title'>
                        {translations?.aisles?.title ?? ''}
                    </p>
                </div>
                <div>
                    <button className='button outlined primary report'
                            onClick={showGenerateReportDialog}>
                        <GenerateReportIcon className='me-2'/>
                        {
                            generatingReport
                                ? translations?.actions?.generateReport?.loading ?? ''
                                : translations?.actions?.generateReport?.title ?? ''
                        }
                    </button>
                </div>
            </div>
            <div className={'mt-2'}>
                <BreadCrumbs data={breadcrumbs}/>
            </div>
            <div className='mt-4'>
                {
                    loading
                        ? (
                            <div className='d-flex flex-wrap'>
                                {
                                    Array(10).fill(null).map((e, index) => (
                                        <div key={index} className={'loading location-card'}>
                                            <div/>
                                        </div>
                                    ))
                                }
                            </div>
                        )
                        : aisles?.length < 1
                            ? <NoEntries
                                text={translations?.aisles?.noEntries ?? ''}
                                includeButton
                                buttonText={translations?.aisles?.create}
                                buttonProps={{
                                    className: 'button primary px-4',
                                    disabled: generatingReport,
                                    onClick: showAddAisleDialog
                                }}
                            />
                            : <TransitionGroup className='d-flex flex-wrap'>
                                {
                                    aisles?.map(location => (
                                            <CSSTransition
                                                classNames={'fade'}
                                                timeout={{enter: 800, exit: 300,}}
                                                key={location.aisle.id}>
                                                <LocationCard
                                                    location={location.aisle}
                                                    onEdit={showEditAisleDialog}
                                                    onRemove={removeAisle}
                                                    type={LocationTypes.aisle}
                                                />
                                            </CSSTransition>
                                        )
                                    )
                                }
                                <CSSTransition
                                    key={'new-location-entry'}
                                    classNames={'fade'}
                                    timeout={{enter: 800, exit: 300,}}>
                                    <button
                                        className={'button primary add-location'}
                                        onClick={showAddAisleDialog}>
                                        <p>
                                            {translations?.actions?.addNew ?? ''}
                                        </p>
                                        <div className='mt-2 text-center'>
                                            <PlusIcon/>
                                        </div>
                                    </button>
                                </CSSTransition>
                            </TransitionGroup>
                }
            </div>
        </div>
    )
};

export default AislesViews;
